import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Share2, RefreshCw, MessageCircle, Sparkles, Mail } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Progress } from "./ui/progress";
import { Checkbox } from "./ui/checkbox";
import {
  Factory, Building2, ShoppingBag, HeartPulse, Cog, Hotel, Truck, Landmark, Cpu, UtensilsCrossed,
  Scale, Palette, Newspaper, GraduationCap, Ruler, Sparkles as SparklesIcon, Dumbbell, PartyPopper,
  Leaf, Sprout, Car, Coins, Users, Briefcase,
} from "lucide-react";
import { useLang, WHATSAPP_LINK, HAS_WHATSAPP } from "../contexts/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_ICONS = {
  oil_gas: Factory, construction: Building2, retail: ShoppingBag, healthcare: HeartPulse,
  manufacturing: Cog, hospitality: Hotel, logistics: Truck, financial: Landmark, ict: Cpu, fnb: UtensilsCrossed,
  professional_services: Scale, creative_agencies: Palette, media: Newspaper, education: GraduationCap,
  architecture: Ruler, beauty_wellness: SparklesIcon, fitness: Dumbbell, events: PartyPopper,
  renewables: Leaf, agritech: Sprout, automotive: Car, fintech: Coins, hrtech: Users, family_office: Briefcase,
};
const ORDER = [
  "oil_gas", "construction", "retail", "healthcare", "manufacturing", "hospitality",
  "logistics", "financial", "ict", "fnb",
  "professional_services", "creative_agencies", "media", "education", "architecture",
  "beauty_wellness", "fitness", "events", "renewables", "agritech",
  "automotive", "fintech", "hrtech", "family_office",
];

function ScoreGauge({ value }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let r = 0;
    const step = Math.max(2, Math.ceil(value / 30)); // ~30 frames total
    const id = setInterval(() => {
      r += step;
      if (r >= value) { r = value; clearInterval(id); }
      setShown(r);
    }, 16);
    return () => clearInterval(id);
  }, [value]);
  const radius = 86;
  const circ = 2 * Math.PI * radius;
  const dash = (shown / 100) * circ;
  return (
    <div className="relative w-56 h-56 grid place-items-center" data-testid="score-gauge">
      <svg className="w-56 h-56 -rotate-90" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r={radius} stroke="rgba(10,17,40,0.08)" strokeWidth="14" fill="none" />
        <circle cx="100" cy="100" r={radius} stroke="#0B4619" strokeWidth="14" fill="none"
                strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} style={{ transition: "stroke-dasharray 0.2s linear" }} />
      </svg>
      <div className="absolute text-center">
        <div className="text-5xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }} data-testid="score-number">{shown}</div>
        <div className="text-xs uppercase tracking-widest text-cb-secondary mt-1">/ {value === 100 ? "100" : "100"}</div>
      </div>
    </div>
  );
}

const HealthCheckQuiz = forwardRef(function HealthCheckQuiz(_, ref) {
  const { t, lang, isRTL } = useLang();
  const [step, setStep] = useState(0); // 0 industry, 1 quiz, 2 form, 3 results
  const [industry, setIndustry] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", role: "", email: "", phone: "", company: "", company_size: "", consent: true });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const sectionRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open: (preselectIndustry) => {
      if (preselectIndustry) {
        pickIndustry(preselectIndustry);
      } else {
        setStep(0);
      }
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    },
  }));

  const pickIndustry = async (id) => {
    setIndustry(id);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quiz/questions/${id}`);
      setQuestions(data.questions);
      setQIdx(0);
      setAnswers({});
      setStep(1);
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      toast.error("Could not load quiz");
    } finally {
      setLoading(false);
    }
  };

  const current = questions[qIdx];
  const totalQ = questions.length;
  const pct = totalQ ? Math.round(((qIdx) / totalQ) * 100) : 0;

  const answer = (val) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.id]: { question_id: current.id, category: current.category, value: val } }));
    setTimeout(() => {
      if (qIdx < totalQ - 1) setQIdx(qIdx + 1);
      else setStep(2);
    }, 150);
  };

  const back = () => {
    if (step === 1 && qIdx > 0) setQIdx(qIdx - 1);
    else if (step === 1) setStep(0);
    else if (step === 2) setStep(1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        industry,
        language: lang,
        answers: Object.values(answers),
      };
      const { data } = await axios.post(`${API}/quiz/submit`, payload);
      setResult(data);
      setStep(3);
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (err) {
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0); setIndustry(null); setQuestions([]); setQIdx(0); setAnswers({}); setResult(null);
  };

  const text = (q) => (lang === "ar" ? q.ar : q.en);

  return (
    <section id="healthcheck" ref={sectionRef} className="section-pad bg-cb-cream relative overflow-hidden">
      <div className="container-cb">
        <div className="max-w-3xl mx-auto text-center">
          <div className="overline mb-4 inline-flex items-center gap-3"><span className="gold-divider" /> {t.quiz.overline} <span className="gold-divider" /></div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-cb-primary">{t.quiz.title}</h2>
          <p className="mt-4 text-cb-secondary text-base lg:text-lg">{t.quiz.sub}</p>
        </div>

        <div className="mt-12 max-w-4xl mx-auto card-cb p-6 sm:p-10" data-testid="quiz-card">
          {/* Step 0: industry */}
          {step === 0 && (
            <div className="fade-up">
              <div className="flex items-baseline justify-between gap-4 mb-2">
                <h3 className="text-xl sm:text-2xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>{t.quiz.pickIndustry}</h3>
                <span className="text-xs uppercase tracking-widest text-cb-secondary">1 / 3</span>
              </div>
              <p className="text-sm text-cb-secondary">{t.quiz.pickIndustrySub}</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {ORDER.map((id) => {
                  const Icon = INDUSTRY_ICONS[id];
                  return (
                    <button key={id} onClick={() => pickIndustry(id)} data-testid={`quiz-industry-${id}`}
                            className="p-4 rounded-xl border border-cb-primary/10 hover:border-cb-emerald hover:bg-cb-emerald hover:text-white text-start transition-all group">
                      <Icon size={20} className="text-cb-emerald group-hover:text-white" />
                      <div className="mt-3 text-sm font-medium" style={{ fontFamily: "Outfit" }}>{t.industriesList[id]}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: questions */}
          {step === 1 && current && (
            <div className="fade-up">
              <div className="flex items-center justify-between gap-4 mb-3">
                <button onClick={back} data-testid="quiz-back" className="text-sm text-cb-secondary inline-flex items-center gap-1 hover:text-cb-primary">
                  {isRTL ? <ArrowRight size={16}/> : <ArrowLeft size={16}/>} {t.quiz.back}
                </button>
                <div className="text-xs uppercase tracking-widest text-cb-secondary">{t.quiz.progress} {qIdx + 1} {t.quiz.of} {totalQ}</div>
              </div>
              <Progress value={pct} className="h-1.5 mb-8" data-testid="quiz-progress" />

              <div className="text-xs uppercase tracking-widest text-cb-emerald font-semibold">{t.quiz.cats[current.category]}</div>
              <h3 className="mt-3 text-2xl sm:text-3xl font-semibold text-cb-primary leading-snug" style={{ fontFamily: "Outfit" }}>
                {text(current)}
              </h3>
              <p className="mt-2 text-sm text-cb-secondary">{t.quiz.qHelp}</p>

              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                {[
                  { v: 2, label: t.quiz.ans.yes, color: "border-cb-emerald" },
                  { v: 1, label: t.quiz.ans.partial, color: "border-cb-gold" },
                  { v: 0, label: t.quiz.ans.no, color: "border-cb-primary/20" },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`quiz-answer-${o.v}`}
                    onClick={() => answer(o.v)}
                    className={`p-5 rounded-xl border-2 ${o.color} bg-white hover:bg-cb-accent-surface text-start transition-all`}
                  >
                    <div className="w-7 h-7 rounded-full bg-cb-accent-surface grid place-items-center text-cb-emerald">
                      {o.v === 2 ? <Check size={14} /> : o.v === 1 ? <Sparkles size={14} /> : <span className="text-xs">—</span>}
                    </div>
                    <div className="mt-3 font-medium text-cb-primary" style={{ fontFamily: "Outfit" }}>{o.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: lead form */}
          {step === 2 && (
            <form onSubmit={submit} className="fade-up" data-testid="quiz-lead-form">
              <div className="flex items-center justify-between gap-4 mb-2">
                <h3 className="text-xl sm:text-2xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>{t.quiz.formTitle}</h3>
                <span className="text-xs uppercase tracking-widest text-cb-secondary">3 / 3</span>
              </div>
              <p className="text-sm text-cb-secondary">{t.quiz.formSub}</p>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.quiz.f.name}</Label>
                  <Input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="lead-name" className="mt-1" />
                </div>
                <div>
                  <Label>{t.quiz.f.role}</Label>
                  <Input value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} data-testid="lead-role" className="mt-1" />
                </div>
                <div>
                  <Label>{t.quiz.f.email}</Label>
                  <Input type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="lead-email" className="mt-1" />
                </div>
                <div>
                  <Label>{t.quiz.f.phone}</Label>
                  <Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="lead-phone" className="mt-1" />
                </div>
                <div>
                  <Label>{t.quiz.f.company}</Label>
                  <Input required value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})} data-testid="lead-company" className="mt-1" />
                </div>
                <div>
                  <Label>{t.quiz.f.size}</Label>
                  <select
                    value={form.company_size}
                    onChange={(e)=>setForm({...form,company_size:e.target.value})}
                    data-testid="lead-size"
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">—</option>
                    {t.quiz.f.sizes.map((s)=> <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2">
                <Checkbox id="consent" checked={form.consent} onCheckedChange={(c)=>setForm({...form,consent:!!c})} data-testid="lead-consent" />
                <label htmlFor="consent" className="text-sm text-cb-secondary">{t.quiz.f.consent}</label>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button type="button" onClick={back} className="text-sm text-cb-secondary inline-flex items-center gap-1 hover:text-cb-primary" data-testid="lead-back">
                  {isRTL ? <ArrowRight size={16}/> : <ArrowLeft size={16}/>} {t.quiz.back}
                </button>
                <button type="submit" disabled={loading || !form.consent} className="btn-primary px-6 py-3 rounded-full font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="lead-submit">
                  {loading ? "…" : t.quiz.submit}
                  {isRTL ? <ArrowLeft size={16}/> : <ArrowRight size={16}/>}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: results */}
          {step === 3 && result && (
            <div className="fade-up" data-testid="quiz-results">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div className="flex flex-col items-center text-center">
                  <ScoreGauge value={result.score} />
                  <div className="mt-4 text-xs uppercase tracking-widest text-cb-secondary">{t.quiz.results.gradeLabel}</div>
                  <div className="mt-1 text-3xl font-semibold text-cb-emerald" style={{ fontFamily: "Outfit" }} data-testid="result-grade">{result.grade}</div>
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>{t.quiz.results.title}</h3>
                  <p className="mt-2 text-cb-secondary">{t.quiz.results.sub}</p>
                  <div className="mt-6 space-y-3">
                    {Object.entries(result.breakdown).map(([cat, val]) => (
                      <div key={cat} data-testid={`breakdown-${cat}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-cb-primary font-medium">{t.quiz.cats[cat]}</span>
                          <span className="text-cb-secondary">{val}/100</span>
                        </div>
                        <div className="h-2 mt-1 bg-cb-accent-surface rounded-full overflow-hidden">
                          <div className="h-full bg-cb-emerald" style={{ width: `${val}%`, transition: "width 0.8s ease" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10 border-t border-cb-primary/10 pt-8">
                <h4 className="text-xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>{t.quiz.results.recsTitle}</h4>
                <div className="mt-4 grid md:grid-cols-3 gap-4">
                  {result.recommendations.map((r, i) => (
                    <div key={i} className="p-5 rounded-xl bg-cb-accent-surface border border-cb-primary/5" data-testid={`recommendation-${i}`}>
                      <div className="text-xs uppercase tracking-widest text-cb-emerald font-semibold">{t.quiz.cats[r.category]}</div>
                      <p className="mt-3 text-sm text-cb-primary leading-relaxed">{lang === "ar" ? r.ar : r.en}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {HAS_WHATSAPP && (
                  <a href={result.whatsapp_share_url || WHATSAPP_LINK(`Hi Capital Bay, I just completed the CEO Health Check (score: ${result.score}/100, grade: ${result.grade}). Please send me the detailed PDF.`)} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium" data-testid="results-wa">
                    <MessageCircle size={16} /> {t.quiz.results.cta}
                  </a>
                )}
                {result.report_token && (
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/reports/${result.report_token}/email`);
                        toast.success(`Report emailed to ${data.to}`);
                      } catch (e) {
                        toast.error("Couldn't send email. Try again.");
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium ${HAS_WHATSAPP ? "border border-cb-emerald text-cb-emerald hover:bg-cb-emerald hover:text-white" : "btn-primary text-white"} transition-colors`}
                    data-testid="results-email"
                  >
                    <Mail size={16} /> {lang === "ar" ? "أرسل التقرير المفصل بالبريد" : "Email the detailed PDF"}
                  </button>
                )}
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium border border-cb-primary/15 text-cb-primary hover:bg-cb-primary hover:text-white transition-colors" data-testid="results-share">
                  <Share2 size={16} /> {t.quiz.results.share}
                </a>
                <button onClick={restart} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-cb-secondary hover:text-cb-primary" data-testid="results-restart">
                  <RefreshCw size={16} /> {t.quiz.results.restart}
                </button>
              </div>

              <div className="mt-6 text-xs text-cb-secondary inline-flex items-center gap-2">
                <Mail size={12} /> {lang === "ar"
                  ? "أرسلنا لك بالبريد نسخة مفصلة من التقرير. اضغط الزر أعلاه لإعادة الإرسال في أي وقت."
                  : "We've already emailed you the detailed PDF. Tap the button above to resend anytime."}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

export default HealthCheckQuiz;
